import { useState, useEffect } from 'react';
import { Mail, Loader2, Send } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export default function BackupEmailCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/backup/email-settings').then((r) => setCfg(r.data)).catch(() => toast.error('Errore caricamento impostazioni email'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/backup/email-settings', {
        backup_email_enabled: cfg.backup_email_enabled,
        backup_email: cfg.backup_email,
        backup_site_url: cfg.backup_site_url,
      });
      setCfg(r.data);
      toast.success('Impostazioni backup email salvate');
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post('/admin/backup/email/send-now', {}, { timeout: 60000 });
      setCfg(r.data.settings);
      toast.success(`Email inviata a ${r.data.settings.backup_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio fallito');
      api.get('/admin/backup/email-settings').then((r) => setCfg(r.data)).catch(() => {});
    } finally {
      setSending(false);
    }
  };

  if (!cfg) return null;

  return (
    <Card className="lg:col-span-2" data-testid="backup-email-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Mail size={18} /> Backup settimanale via email</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Ogni 7 giorni riceverai un'email con un link sicuro (valido 7 giorni) per scaricare il backup completo del sito.
        </p>
        <div className="flex items-center gap-3">
          <Switch checked={cfg.backup_email_enabled} onCheckedChange={(v) => setCfg({ ...cfg, backup_email_enabled: v })} data-testid="backup-email-enabled-switch" />
          <Label>{cfg.backup_email_enabled ? 'Attivo' : 'Disattivato'}</Label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-2 block">Email destinatario</Label>
            <Input type="email" value={cfg.backup_email} onChange={(e) => setCfg({ ...cfg, backup_email: e.target.value })} data-testid="backup-email-input" />
          </div>
          <div>
            <Label className="mb-2 block">Indirizzo del sito (per il link)</Label>
            <Input value={cfg.backup_site_url} onChange={(e) => setCfg({ ...cfg, backup_site_url: e.target.value })} data-testid="backup-site-url-input" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="backup-email-last-sent">
          {cfg.backup_email_last_sent
            ? `Ultimo invio: ${new Date(cfg.backup_email_last_sent).toLocaleString('it-IT')} (${cfg.backup_email_last_status === 'sent' ? 'riuscito' : 'fallito'})`
            : 'Nessun invio ancora effettuato'}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={save} disabled={saving} data-testid="backup-email-save-btn">
            {saving && <Loader2 size={16} className="mr-2 animate-spin" />}Salva
          </Button>
          <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="backup-email-send-now-btn">
            {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}Invia ora
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
